import { Box, Button, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages, WelcomeOfferNotificationData } from "~/core";
import { isInternalLink } from "~/core/utils/action.utils";
import { Close } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type WelcomeOfferNotificationProps = {
  data: WelcomeOfferNotificationData;
  onClose?: () => void;
  isControlInactive: (control: Action) => boolean;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
};
export const WelcomeOfferNotification: FC<WelcomeOfferNotificationProps> = ({
  data,
  isControlInactive,
  getUrl,
  onCallAction,
  onClose,
}) => {
  const mBg = data?.background?.image?.id;
  const mBgColor = data?.background?.color;
  const mdBg = data?.background?.tablet_image?.id ?? mBg;
  const dBg = data?.background?.desktop_image?.id ?? mdBg;
  const { getDirectusImage } = useDirectus();
  const controlAction = data?.action;
  const action = controlAction?.action?.[0];

  const isActionActive = action ? !isControlInactive(controlAction) : false;
  let href: string | undefined;
  const mobileImage = data?.image?.image?.id ?? "";
  const tabletImage = data?.image?.tablet_image?.id ?? mobileImage;
  const desktopImage = data?.image?.desktop_image?.id ?? tabletImage;

  const mobileImageWidth = data?.image?.mobile_image_width ?? undefined;
  const tabletImageWidth = data?.image?.tablet_image_width ?? mobileImageWidth;
  const desktopImageWidth =
    data?.image?.desktop_image_width ?? tabletImageWidth;

  const mobileImageHeight = data?.image?.mobile_image_height ?? undefined;
  const tabletImageHeight =
    data?.image?.tablet_image_height ?? mobileImageHeight;
  const desktopImageHeight =
    data?.image?.desktop_image_height ?? tabletImageHeight;

  if (isInternalLink(action?.item)) {
    href = action?.item?.page ? getUrl(action.item.page) : undefined;
  }
  const mobileBottomSpace = data?.mobile_bottom_spacing ?? 72;
  const tabletBottomSpace = data?.tablet_bottom_spacing ?? mobileBottomSpace;
  const desktopBottomSpace = data?.desktop_bottom_spacing ?? tabletBottomSpace;
  const onPress = (): void => {
    if (action && isActionActive) {
      onCallAction(controlAction);
    }
    onClose?.();
  };
  return (
    <Flex
      position={{
        type: "fixed",
        left: 16,
        right: 16,
        bottom: mobileBottomSpace,
        zIndex: 30,
      }}
      m={{ mx: "auto" }}
      p={{ px: 16, py: 16 }}
      minWidth={312}
      maxWidth={512}
      bg={mBg ? getDirectusImage(mBg) : (mBgColor ?? "")}
      justify="center"
      flexDirection="column"
      border={{ radius: "banner" }}
      animation={{
        name: "keyframes",
        duration: 0.35,
        transition: "linear",
        direction: "forwards",
        keyframes: {
          "0%": { opacity: 0, transform: "translateY(100%)" },
          "50%": { opacity: 1, transform: "translateY(50%)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
      md={{
        bg: mdBg,
        maxWidth: 640,
        width: "100%",
        position: { bottom: tabletBottomSpace },
      }}
      d={{ bg: dBg, position: { bottom: desktopBottomSpace } }}
    >
      <Flex gap={16} m={{ mb: 16 }} md={{ m: { mb: 0 } }} align="center">
        <Image
          value={getDirectusImage(mobileImage, {
            width: mobileImageWidth ? mobileImageWidth * 2 : undefined,
            height: mobileImageHeight ? mobileImageHeight * 2 : undefined,
            format: "webp",
            quality: 85,
          })}
          image={{
            alt: data?.image?.alternative_text ?? "",
          }}
          mdValue={getDirectusImage(tabletImage, {
            width: tabletImageWidth ? tabletImageWidth * 2 : undefined,
            height: tabletImageHeight ? tabletImageHeight * 2 : undefined,
            format: "webp",
            quality: 85,
          })}
          dValue={getDirectusImage(desktopImage, {
            width: desktopImageWidth ? desktopImageWidth * 2 : undefined,
            height: desktopImageHeight ? desktopImageHeight * 2 : undefined,
            format: "webp",
            quality: 85,
          })}
          width={mobileImageWidth}
          height={mobileImageHeight}
          md={{ width: tabletImageWidth, height: tabletImageHeight }}
          d={{ width: desktopImageWidth, height: desktopImageHeight }}
        />
        <Flex justify="space-between" width="100%">
          <Box as="span">
            <Typography
              fontSize="body.mobile.small"
              lineHeight="body.mobile.small"
              md={{
                fontSize: "body.mobile.medium",
                lineHeight: "body.mobile.medium",
              }}
              m={{ mb: 4 }}
            >
              {data?.title}
            </Typography>
            <Typography
              maxWidth={165}
              fontWeight="mobile.bold"
              fontSize="title.mobile.xsmall"
              lineHeight="title.mobile.xsmall"
              md={{
                maxWidth: 190,
                fontSize: "title.desktop.small",
                lineHeight: "title.desktop.small",
              }}
            >
              {data?.description}
            </Typography>
          </Box>
          <Flex
            flexDirection={"column"}
            justify="space-between"
            align={"flex-end"}
          >
            {onClose && (
              <Button
                p={{ py: 0, px: 0 }}
                variant="tertiary"
                onPress={onClose}
                maxHeight={24}
                maxWidth={24}
              >
                <Close opacity={0.5} />
              </Button>
            )}
            {isActionActive && (
              <Button
                display="hidden"
                md={{ display: "block" }}
                onPress={onPress}
                href={isActionActive ? href : undefined}
                fontWeight="desktop.bold"
                fontSize="button.desktop.xsmall"
                lineHeight="button.desktop.xsmall"
              >
                {data?.action_text}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
      {isActionActive && (
        <Button
          onPress={onPress}
          href={isActionActive ? href : undefined}
          fontWeight="mobile.bold"
          fontSize="button.mobile.small"
          lineHeight="button.mobile.small"
          md={{ display: "hidden" }}
        >
          {data?.action_text}
        </Button>
      )}
    </Flex>
  );
};
